// src/pages/ResetPasswordPage.tsx
import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { authAPI } from '../services/api';
import PasswordField from '../components/common/PasswordField';
import { SignInButton } from '../components/common/Buttons';
import { colors } from '../theme/theme';
import { InlineMessage } from '../components/common/InlineMessage';

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[300],
  paddingBottom: '120px',

  '@media (max-width: 768px)': {
    paddingBottom: '8rem',
  },
}));

const PagePaper = styled(Paper)({
  padding: '1rem',
  width: '100%',
  maxWidth: '400px',
  borderRadius: '12px',
  border: `2px solid ${colors.primary.main}`,
  boxShadow: '0 8px 14px rgba(0,0,0,0.25)',

  '@media (max-width: 768px)': {
    padding: '1rem 1rem',
    maxWidth: '90%',
    borderRadius: '12px',
  },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { borderRadius: '2px' },
  },
  '@media (max-width: 768px)': {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
};

const backLinkStyle = {
  color: colors.primary.main,
  textDecoration: 'none',
  fontSize: '1.4rem',
};

const ResetPasswordPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    newPassword.length > 0 && confirmPassword.length > 0 && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) return;

    setIsLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setSubmitted(true);
    } catch (err: any) {
      if (err instanceof TypeError || err?.name === 'TypeError') {
        setError(
          'Unable to reach the server. Please check your connection and try again.'
        );
      } else {
        setError(
          err?.response?.data?.message ||
            'This reset link is invalid or has expired.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <PagePaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : 'inherit',
            }}
          >
            Choose a new password
          </Typography>
        </Box>

        {!token ? (
          <>
            <InlineMessage
              message="This reset link is invalid. Please request a new one."
              severity="error"
            />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <RouterLink to="/forgot-password" style={backLinkStyle}>
                Request a new link
              </RouterLink>
            </Box>
          </>
        ) : submitted ? (
          <>
            <InlineMessage
              message="Your password has been reset. You can now log in."
              severity="success"
            />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <RouterLink to="/login" style={backLinkStyle}>
                Back to login
              </RouterLink>
            </Box>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <InlineMessage message={error} severity="error" />}

            <PasswordField
              fullWidth
              label="New Password"
              variant="outlined"
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              disabled={isLoading}
              autoFocus
              sx={fieldSx}
            />
            <PasswordField
              fullWidth
              label="Confirm New Password"
              variant="outlined"
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={isLoading}
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={
                confirmPassword.length > 0 && !passwordsMatch
                  ? 'Passwords do not match'
                  : ''
              }
              sx={fieldSx}
            />
            <SignInButton
              text={isLoading ? 'Resetting...' : 'Reset Password'}
              isLoading={isLoading}
              disabled={isLoading || !canSubmit}
            />
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <RouterLink to="/login" style={backLinkStyle}>
                Back to login
              </RouterLink>
            </Box>
          </form>
        )}
      </PagePaper>
    </PageContainer>
  );
};

export default ResetPasswordPage;
